import { SCOrderListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency } from "@sellar-io/common";
import dayjs from "dayjs";
import {
  getOrderStage,
  getOrderStageBadge,
  ListElement,
  ProfilePicture,
} from "../../../../../../components";

import { InfiniteList } from "../../../../../../components/infiniteLoading/InfiniteList";
import { useSCOrderList } from "../../../../../../hooks/api/v2/sellarCreditManagement/useSCOrderList";
import { TabPaymentStatusBadge } from "./TabPaymentStatusBadge";
import { DataButton, H5 } from "@sellar-io/components";
import { useGetTwoPaymentLink } from "../hooks/useGetTwoPaymentLink";
import { canOrderBePaid } from "../utils/orderPaymentStatus";

const SellarCreditOrderCard = ({
  order,
  setSelectedOrder,
  setActive,
}: {
  order: SCOrderListResponseType[number];
  setSelectedOrder: (order: SCOrderListResponseType[number]) => void;
  setActive: (value: boolean) => void;
}) => {
  const getPaymentLinkMutation = useGetTwoPaymentLink();

  const orderCanBePaid = canOrderBePaid(order);

  return (
    <ListElement
      onClick={() => {
        setSelectedOrder(order);
        setActive(true);
      }}
    >
      <ProfilePicture
        logoUrl={order.Supplier.logoUrl}
        name={order.Supplier.name}
        size="large"
      />
      <div className={"flex flex-col gap-1"}>
        <span className="text-xs text-zinc-400">
          Order #{order.id} placed on{" "}
          {dayjs(order.createdAt).format("DD/MM/YYYY")}
        </span>
        <H5>
          {order.Supplier.name} &ndash;&nbsp;
          {formatCurrency(order.price)}
        </H5>
        <span className="text-sm">
          {order.Business.name} • {order.orderPlacedByName}
        </span>
        <div className="mt-2 flex gap-2">
          {getOrderStageBadge(getOrderStage(order))}
          <TabPaymentStatusBadge order={order} />
        </div>
      </div>
      {orderCanBePaid && (
        <div className="ml-auto flex items-center">
          <DataButton
            disabled={getPaymentLinkMutation.isPending}
            border
            onClick={(e) => {
              e.stopPropagation();
              getPaymentLinkMutation
                .mutateAsync(order.id)
                .then((result) => {
                  window.open(result.link, "_blank");
                })
                .catch((error) => {
                  console.error(error);
                });
            }}
          >
            Pay
          </DataButton>
        </div>
      )}
    </ListElement>
  );
};

export const SellarCreditOrderCardList = ({
  queryParams,
  setSelectedOrder,
  setActive,
}: {
  queryParams: any;
  setSelectedOrder: (order: SCOrderListResponseType[number]) => void;
  setActive: (value: boolean) => void;
}) => {
  const orderQuery = useSCOrderList(
    { ...queryParams, limit: 5 },
    {
      refetchOnWindowFocus: false,
    },
  );

  const orders =
    orderQuery.data?.pages.flatMap((page) => {
      return page.map((page) => page);
    }) ?? [];

  return (
    <>
      <InfiniteList query={orderQuery}>
        {orders.map((order, idx) => (
          <SellarCreditOrderCard
            key={idx}
            order={order}
            setSelectedOrder={setSelectedOrder}
            setActive={setActive}
          />
        ))}
      </InfiniteList>
      {orders.length === 0 && (
        <div className="w-full p-4">
          <span className="text-sm">Your search returned 0 results</span>
        </div>
      )}
    </>
  );
};
